import * as CryptoJS from 'crypto-js';
import * as forge from 'node-forge';

export class ArkEncryptionService {
	private RK = `<RSAKeyValue><Exponent>AQAB</Exponent><Modulus>0oHP8jOhMDkCQiTzgXfrIpOdivKDpqgynCvifG1TQfxFtWwN1K5CtTmbZwPkTvz3bbIeQPdulBuZIPW3Zps/KWllNMBm9XhP7+//06OBA8/erliMGwVDcYMVfAKP6wUy4485txYcg40fZVovP8mSz/FYjaUqG3GrXN8rGYa5/RfZHGZQKlmhHBLq6oVdcHViMC9VrRg85y14+tOIQTpsrdrG4YYkHUEzZQiP1BFQBgo+2xdTDRBKrYhlOJ6mcUCbEIe1heTW1zmKfpjopTon+/ObKjcjATo6KQC8RhD859RzU/7KVY1Vf+SOObX0mKTYBXA8ENgts+e/Vkg0s25DFQ==</Modulus></RSAKeyValue>`;

	private aIV = CryptoJS.enc.Utf8.parse('d80/cxLQY5GGHOo4');

	// Generate random 16-byte AES key
	generateAesKey(): string {
		const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		let key = '';
		for (let i = 0; i < 16; i++) {
			key += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		return key;
	}

	// Encrypt data using AES
	encryptAes(data: any, aesKey: string): string {
		const key = CryptoJS.enc.Utf8.parse(aesKey);
		const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, {
			iv: this.aIV,
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7,
		});
		return encrypted.toString();
	}

	// Encrypt AES key using RSA
	encryptRsa(aesKey: string): string {
		// Extract modulus and exponent from XML
		const modulusMatch = this.RK.match(/<Modulus>(.+)<\/Modulus>/);
		const exponentMatch = this.RK.match(/<Exponent>(.+)<\/Exponent>/);

		if (!modulusMatch || !exponentMatch) {
			throw new Error('Invalid RSA key format');
		}

		const modulusB64 = modulusMatch[1];
		const exponentB64 = exponentMatch[1];

		const modulus = forge.util.createBuffer(forge.util.decode64(modulusB64), 'raw');
		const exponent = forge.util.createBuffer(forge.util.decode64(exponentB64), 'raw');

		const n = new forge.jsbn.BigInteger(modulus.toHex(), 16);
		const e = new forge.jsbn.BigInteger(exponent.toHex(), 16);

		// Build public key
		const publicKey = forge.pki.setRsaPublicKey(n, e);

		// Convert AES key to UTF-16 LE (C# UnicodeEncoding)
		const utf16leBytes: number[] = [];
		for (let i = 0; i < aesKey.length; i++) {
			const code = aesKey.charCodeAt(i);
			utf16leBytes.push(code & 0xff, code >> 8); // little-endian
		}
		const utf16leString = String.fromCharCode(...utf16leBytes);
		// Encrypt AES key using RSA
		const encryptedKey = publicKey.encrypt(utf16leString, 'RSAES-PKCS1-V1_5');

		return forge.util.encode64(encryptedKey);
	}

	// Build encrypted payload
	buildSecurePayload(data: any): { key: string; data: string } {
		const aesKey = this.generateAesKey();
		const encryptedData = this.encryptAes(data, aesKey);
		const encryptedKey = this.encryptRsa(aesKey);
		return { key: encryptedKey, data: encryptedData };
	}
}

// Optionally, export a singleton instance for convenience
export const arkEncryptionService = new ArkEncryptionService();
